import { DateTime } from 'luxon';
import {
  Reservation,
  ReservationPhone,
  TripLeg,
  TripLegAddress,
} from '../data/models/reservation.model';
import {
  TransportReservation,
  TransportReservationResponse,
  TransportTripLeg,
} from '../data/models/transport-reservation.model';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import omitBy from 'lodash/omitBy';
import {
  TransportationAttribute,
  TransportationAttributeGroup,
} from '@/data/models/transportation-attribute.model';

function mapTripLegAddress(
  addressLine1: string,
  addressLine2: string,
  city: string,
  stateCode: string,
  zipCode: string,
  latitude: string,
  longitude: string,
  timeZoneIANA: string,
  county?: string,
): TripLegAddress {
  return omitBy(
    {
      addressLine1,
      addressLine2: addressLine2 || undefined,
      city,
      stateCode,
      zipCode,
      county,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
      timeZone: timeZoneIANA,
    },
    isNil,
  ) as unknown as TripLegAddress;
}

function formatCoordinate(value: number | string | undefined | null): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

function mapTripLegPhone(
  phoneNumber: string | null | undefined,
  phoneExtension: string | null | undefined,
): ReservationPhone | undefined {
  if (!phoneNumber) {
    return undefined;
  }
  return {
    phoneNumberTypeBC: 'NA',
    passengerPhoneUseBC: 'RESERVATION',
    phoneNumber,
    phoneExtension: phoneExtension || undefined,
    hasSMSConsent: false,
  };
}

export function mergeTripLegIntoTransportTripLeg(
  existing: TransportTripLeg,
  updated: TripLeg,
): TransportTripLeg {
  const synced = updated.tripLeg;
  const pickUp = updated.pickUpAddress;
  const dropOff = updated.dropOffAddress;

  return {
    ...existing,
    tripLegID: updated.tripLegID ?? synced?.tripLegID ?? existing.tripLegID,
    tripLegReferenceID:
      updated.tripLegReferenceID ??
      synced?.tripLegReferenceID ??
      existing.tripLegReferenceID,
    tripLegSequence: updated.tripLegSequence ?? existing.tripLegSequence,
    appointmentDate:
      updated.appointmentDate ??
      synced?.appointmentDate ??
      existing.appointmentDate,
    appointmentTimeUTC:
      updated.appointmentTimeUTC ??
      synced?.appointmentTimeUTC ??
      existing.appointmentTimeUTC,
    pickUpDueTimeUTC:
      updated.pickUpDueTimeUTC ??
      synced?.pickUpDueTimeUTC ??
      existing.pickUpDueTimeUTC,
    transportationProviderID:
      updated.transportationProviderID ??
      synced?.transportationProviderID ??
      existing.transportationProviderID,
    driverID: updated.driverID ?? synced?.driverID ?? existing.driverID,
    vehicleID: synced?.vehicleID ?? existing.vehicleID,
    isWillCall: updated.isWillCall,
    companionCount: updated.companionCount,
    isUnderDiscussion:
      updated.isUnderDiscussion ??
      synced?.isUnderDiscussion ??
      existing.isUnderDiscussion,
    isException:
      updated.isException ?? synced?.isException ?? existing.isException,
    isEscalated:
      updated.isEscalated ?? synced?.isEscalated ?? existing.isEscalated,
    isInternal: updated.isInternal ?? synced?.isInternal ?? existing.isInternal,
    estimatedDistanceMiles:
      updated.estimatedDistanceMiles ??
      synced?.estimatedDistanceMiles ??
      existing.estimatedDistanceMiles,
    invoiceDistanceMiles:
      updated.invoiceDistanceMiles ??
      synced?.invoiceDistanceMiles ??
      existing.invoiceDistanceMiles,
    payableAmount:
      updated.payableAmount ?? synced?.payableAmount ?? existing.payableAmount,
    tripLegStatusID: synced?.tripLegStatusID ?? existing.tripLegStatusID,
    tripLegState:
      updated.state ?? synced?.tripLegState ?? existing.tripLegState,
    tripLegStatusBC:
      updated.tripLegStatusBC ??
      updated.status?.toUpperCase() ??
      existing.tripLegStatusBC,
    driverWaitTimeSeconds:
      synced?.driverWaitTimeSeconds ?? existing.driverWaitTimeSeconds,
    transportationTypeID:
      synced?.transportationTypeID ?? existing.transportationTypeID,
    pickUpNotes:
      updated.pickUpNote ?? synced?.pickUpNotes ?? existing.pickUpNotes,
    dropOffNotes:
      updated.dropOffNote ?? synced?.dropOffNotes ?? existing.dropOffNotes,
    pickUpAddressLine1:
      synced?.pickUpAddressLine1 ??
      pickUp?.addressLine1 ??
      existing.pickUpAddressLine1,
    pickUpAddressLine2:
      synced?.pickUpAddressLine2 ??
      pickUp?.addressLine2 ??
      existing.pickUpAddressLine2,
    pickUpCity: synced?.pickUpCity ?? pickUp?.city ?? existing.pickUpCity,
    pickUpStateCode:
      synced?.pickUpStateCode ?? pickUp?.stateCode ?? existing.pickUpStateCode,
    pickUpZipCode:
      synced?.pickUpZipCode ?? pickUp?.zipCode ?? existing.pickUpZipCode,
    pickUpCounty:
      synced?.pickUpCounty ?? pickUp?.county ?? existing.pickUpCounty,
    pickUpAddressLatitude:
      synced?.pickUpAddressLatitude ??
      (formatCoordinate(pickUp?.latitude) || existing.pickUpAddressLatitude),
    pickUpAddressLongitude:
      synced?.pickUpAddressLongitude ??
      (formatCoordinate(pickUp?.longitude) || existing.pickUpAddressLongitude),
    pickUpPassengerPhoneNumber:
      updated.pickUpPhone?.phoneNumber ??
      synced?.pickUpPassengerPhoneNumber ??
      existing.pickUpPassengerPhoneNumber,
    pickUpPassengerPhoneExtension:
      updated.pickUpPhone?.phoneExtension ??
      synced?.pickUpPassengerPhoneExtension ??
      existing.pickUpPassengerPhoneExtension,
    dropOffAddressLine1:
      synced?.dropOffAddressLine1 ??
      dropOff?.addressLine1 ??
      existing.dropOffAddressLine1,
    dropOffAddressLine2:
      synced?.dropOffAddressLine2 ??
      dropOff?.addressLine2 ??
      existing.dropOffAddressLine2,
    dropOffCity: synced?.dropOffCity ?? dropOff?.city ?? existing.dropOffCity,
    dropOffStateCode:
      synced?.dropOffStateCode ??
      dropOff?.stateCode ??
      existing.dropOffStateCode,
    dropOffZipCode:
      synced?.dropOffZipCode ?? dropOff?.zipCode ?? existing.dropOffZipCode,
    dropOffCounty:
      synced?.dropOffCounty ?? dropOff?.county ?? existing.dropOffCounty,
    dropOffAddressLatitude:
      synced?.dropOffAddressLatitude ??
      (formatCoordinate(dropOff?.latitude) || existing.dropOffAddressLatitude),
    dropOffAddressLongitude:
      synced?.dropOffAddressLongitude ??
      (formatCoordinate(dropOff?.longitude) ||
        existing.dropOffAddressLongitude),
    dropOffPassengerPhoneNumber:
      updated.dropOffPhone?.phoneNumber ??
      synced?.dropOffPassengerPhoneNumber ??
      existing.dropOffPassengerPhoneNumber,
    dropOffPassengerPhoneExtension:
      updated.dropOffPhone?.phoneExtension ??
      synced?.dropOffPassengerPhoneExtension ??
      existing.dropOffPassengerPhoneExtension,
    pickUpAddressTimeZoneIANA:
      updated.pickUpAddressTimeZoneIANA ??
      pickUp?.timeZone ??
      existing.pickUpAddressTimeZoneIANA,
    dropOffAddressTimeZoneIANA:
      dropOff?.timeZone ?? existing.dropOffAddressTimeZoneIANA,
  };
}

type NestedTransportationAttribute = TransportationAttribute & {
  children?: NestedTransportationAttribute[];
};

function buildTransportationAttributeTree(
  items: TransportationAttribute[],
): TransportationAttribute[] {
  // Build a map of categoryBC -> items belonging to that category
  const categoryMap = new Map<string, TransportationAttribute[]>();

  for (const item of items) {
    const cat = item.transportationAttributeCategoryBC;
    if (!categoryMap.has(cat)) {
      categoryMap.set(cat, []);
    }
    categoryMap.get(cat)!.push(item);
  }

  // Determine which categories are "children" (referenced by some item's childTransportationAttributeCategoryBC)
  const childCategories = new Set(
    items
      .map((item) => item.childTransportationAttributeCategoryBC)
      .filter((cat): cat is string => cat !== undefined),
  );

  // Only return top-level items (whose own category is NOT a child of anyone)
  return items
    .filter(
      (item) => !childCategories.has(item.transportationAttributeCategoryBC),
    )
    .map((item) =>
      attachTransportationAttributeChildren(item, categoryMap, childCategories),
    );
}

function attachTransportationAttributeChildren(
  item: TransportationAttribute,
  categoryMap: Map<string, TransportationAttribute[]>,
  childCategories: Set<string>,
): NestedTransportationAttribute {
  if (!item.childTransportationAttributeCategoryBC) {
    return item;
  }

  const children = (
    categoryMap.get(item.childTransportationAttributeCategoryBC) ?? []
  ).map((child) =>
    attachTransportationAttributeChildren(child, categoryMap, childCategories),
  );

  return { ...item, children };
}

function unwindAttributeTree(
  items: NestedTransportationAttribute[],
): NestedTransportationAttribute[][] {
  const paths: NestedTransportationAttribute[][] = [];

  function traverse(
    item: NestedTransportationAttribute,
    currentPath: NestedTransportationAttribute[],
  ) {
    const path = [...currentPath, item];

    if (!item.children || item.children.length === 0) {
      paths.push(path);
      return;
    }

    for (const child of item.children) {
      traverse(child, path);
    }
  }

  for (const item of items) {
    traverse(item, []);
  }

  return paths;
}

export function mapTripLegTransportationAttributes(
  legTransportationAttributeIDs: string[],
  transportationAttributes?: TransportationAttribute[],
) {
  const allAttributesByUUID = keyBy(
    transportationAttributes ?? [],
    'transportationAttributeUUID',
  );

  const legAttributes = (legTransportationAttributeIDs ?? [])
    .map((attrUUID) => allAttributesByUUID[attrUUID])
    .filter(Boolean);

  const mobilityAttributes = legAttributes.filter(
    (attr) => attr.transportationAttributeCategoryBC === 'TRANSPORTMOBILITY',
  );
  const hierarchyAttributes = legAttributes.filter(
    (attr) => attr.transportationAttributeCategoryBC !== 'TRANSPORTMOBILITY',
  );

  // Build parent -> child chains for the remaining attributes (the ambulatory chain
  // plus any secondary attributes), ordering primary groups before secondary ones.
  const hierarchyPaths = unwindAttributeTree(
    buildTransportationAttributeTree(hierarchyAttributes),
  ).sort((a, b) => {
    const primaryA =
      a[0]?.grouping === TransportationAttributeGroup.PRIMARY ? 0 : 1;
    const primaryB =
      b[0]?.grouping === TransportationAttributeGroup.PRIMARY ? 0 : 1;

    if (primaryA !== primaryB) return primaryA - primaryB;

    return (a[0]?.displayOrder ?? Infinity) - (b[0]?.displayOrder ?? Infinity);
  });

  // Mobility first (kept as a distinct array even if empty so the ambulatory chain
  // stays at index 1), then the ordered hierarchy paths. Return only the UUIDs.
  return [
    mobilityAttributes.map((attr) => attr.transportationAttributeUUID!),
    ...hierarchyPaths.map((path) =>
      path.map((attr) => attr.transportationAttributeUUID!),
    ),
  ];
}

export function mapTransportTripLeg(
  leg: TransportTripLeg,
  reservation: TransportReservation | null,
  transportationAttributes?: TransportationAttribute[],
): TripLeg {
  let attributesToFormat: string[] = [];

  // array of arrays of strings: [["uuid1"], ["uuid2", "uuid3"]]
  // after saving correct format gets merge into the tripleg
  if (Array.isArray(leg?.transportationAttributes?.[0])) {
    const flattened = leg?.transportationAttributes?.flat();

    if (typeof flattened[0] === 'string') {
      attributesToFormat = flattened as unknown as string[];
    }
  } else {
    // flat array of objects: [{ transportationAttributeUUID: "uuid1", ... }]
    // happens on /reservations fetch
    attributesToFormat =
      leg?.transportationAttributes?.map(
        (attr) => attr.transportationAttributeUUID,
      ) || [];
  }

  return {
    reservation: {
      reservationReferenceID: reservation?.reservationReferenceID ?? '',
      standingOrderUUID: reservation?.standingOrderUUID ?? '',
    },
    tripLegID: leg.tripLegID,
    tripLegReferenceID: leg.tripLegReferenceID,
    tripLegSequence: leg.tripLegSequence,
    appointmentDate: leg.appointmentDate,
    appointmentDateTime: leg.appointmentTimeUTC
      ? (DateTime.fromISO(leg.appointmentTimeUTC)
          .setZone(leg.appointmentTimeZoneIANA)
          .toISO({ includeOffset: false }) ?? undefined)
      : undefined,
    appointmentTimeUTC: leg.appointmentTimeUTC ?? undefined,
    pickUpDueDateTime: leg.pickUpDueTimeUTC
      ? (DateTime.fromISO(leg.pickUpDueTimeUTC)
          .setZone(leg.pickUpAddressTimeZoneIANA)
          .toISO({ includeOffset: false }) ?? undefined)
      : undefined,
    pickUpDueTimeUTC: leg.pickUpDueTimeUTC ?? undefined,
    pickUpAddressTimeZoneIANA: leg.pickUpAddressTimeZoneIANA,
    passengerUUID: reservation?.passenger?.passengerUUID ?? '',
    purposeCodeUUID: leg.clientPurposeCode?.clientPurposeCodeUUID ?? '',
    isWillCall: leg.isWillCall,
    companionCount: leg.companionCount,
    isException: leg.isException,
    isEscalated: leg.isEscalated,
    isUnderDiscussion: leg.isUnderDiscussion,
    isInternal: leg.isInternal,
    estimatedDistanceMiles: leg.estimatedDistanceMiles ?? undefined,
    invoiceDistanceMiles: leg.invoiceDistanceMiles ?? undefined,
    payableAmount: leg.payableAmount ?? undefined,
    tripLegStatusBC: leg.tripLegStatusBC,
    state: leg.tripLegState,
    status: leg.tripLegStatusBC?.toLowerCase(),
    overrides: [],
    transportationAttributes: mapTripLegTransportationAttributes(
      attributesToFormat,
      transportationAttributes,
    ),
    tripLegPassengerNoteTypeBCs: [],
    tripLegDriverNoteTypeBCs: [],
    metadata: { roundTrip: false },
    pickUpAddress: mapTripLegAddress(
      leg.pickUpAddressLine1,
      leg.pickUpAddressLine2,
      leg.pickUpCity,
      leg.pickUpStateCode,
      leg.pickUpZipCode,
      leg.pickUpAddressLatitude,
      leg.pickUpAddressLongitude,
      leg.pickUpAddressTimeZoneIANA,
      leg.pickUpCounty,
    ),
    dropOffAddress: mapTripLegAddress(
      leg.dropOffAddressLine1,
      leg.dropOffAddressLine2,
      leg.dropOffCity,
      leg.dropOffStateCode,
      leg.dropOffZipCode,
      leg.dropOffAddressLatitude,
      leg.dropOffAddressLongitude,
      leg.dropOffAddressTimeZoneIANA,
      leg.dropOffCounty,
    ),
    pickUpPhone: mapTripLegPhone(
      leg.pickUpPassengerPhoneNumber,
      leg.pickUpPassengerPhoneExtension,
    ),
    dropOffPhone: mapTripLegPhone(
      leg.dropOffPassengerPhoneNumber,
      leg.dropOffPassengerPhoneExtension,
    ),
    pickUpNote: leg.pickUpNotes || undefined,
    dropOffNote: leg.dropOffNotes || undefined,
  };
}

export function mapTransportReservation(
  item: TransportReservation,
): Reservation {
  return {
    reservationReferenceID: item.reservationReferenceID,
    reservationServiceDate: item.reservationServiceDate,
    state: item.reservationState,
    clientBC: '',
    passengerUUID: item.passenger?.passengerUUID ?? '',
    tripLegs: item.tripLegs.map((leg) => mapTransportTripLeg(leg, item)),
  };
}

export function mapTransportReservationResponse(
  page: TransportReservationResponse,
): {
  reservations: Reservation[];
  continuationToken?: string;
} {
  const hasNextPage = page.currentPage < page.totalPages;
  return {
    reservations: page.items.map(mapTransportReservation),
    continuationToken: hasNextPage ? String(page.currentPage + 1) : undefined,
  };
}
